import { signInWithGoogleOAuth } from '@/lib/oauth/google-oauth';
import { useAuthStore } from '@/store/authStore';
import { showToast } from '@/store/toastStore';
import { sanitizeErrorText } from '@/utils/api-error';
import { useCallback, useState } from 'react';

type UseGoogleOAuthOptions = {
  onError?: (error: unknown) => void;
  /**
   * Quando presente, recebe o token do Supabase em vez de trocar a sessão:
   * é assim que a conta logada vincula o Google sem virar outra sessão.
   */
  onToken?: (token: string) => Promise<void>;
};

export function useGoogleOAuth(options?: UseGoogleOAuthOptions) {
  const googleLogin = useAuthStore((state) => state.googleLogin);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const signInWithGoogle = useCallback(async () => {
    try {
      setIsGoogleLoading(true);
      const outcome = await signInWithGoogleOAuth();

      if (outcome.status === 'success') {
        if (options?.onToken) {
          await options.onToken(outcome.accessToken);
        } else {
          await googleLogin(outcome.accessToken);
        }
        return;
      }

      if (outcome.status === 'error') {
        showToast(
          sanitizeErrorText(outcome.message, 'Não foi possível entrar. Tente novamente.'),
          'error',
        );
        options?.onError?.(new Error(outcome.message));
        return;
      }

      if (outcome.status === 'no_tokens') {
        showToast('Não foi possível entrar. Tente novamente.', 'error');
        return;
      }

      if (outcome.status === 'cancelled') {
        showToast('Login com Google cancelado.', 'info');
      }
    } catch (error) {
      console.error('Google OAuth Error:', error);
      showToast('Falha ao tentar logar com Google!', 'error');
      options?.onError?.(error);
    } finally {
      setIsGoogleLoading(false);
    }
  }, [googleLogin, options]);

  return { signInWithGoogle, isGoogleLoading };
}
